<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
]);
</script>

<template>
  <header
    class="sticky top-0 z-20 border-b border-auth-border bg-auth-canvas/95 px-3 dark:border-auth-border-dark dark:bg-auth-canvas-dark/95 sm:px-5 lg:px-6"
  >
    <div
      class="mx-auto flex w-full max-w-6xl items-start justify-between gap-3 py-4 sm:items-center sm:py-5"
    >
      <span
        class="min-w-0 truncate text-xl font-semibold tracking-[-0.025em] text-auth-text dark:text-auth-text-dark sm:text-2xl"
      >
        {{ headerTitle }}
      </span>
      <div
        class="flex flex-shrink-0 flex-col items-end gap-3 sm:flex-row sm:items-center"
      >
        <div v-if="showSearch" class="flex w-full items-center gap-2 sm:w-64">
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-9 !rounded-xl !border-auth-border !bg-auth-soft !py-1 ps-9 pe-3 !text-auth-text placeholder:!text-auth-muted focus:!border-auth-action dark:!border-auth-border-dark dark:!bg-auth-soft-dark dark:!text-auth-text-dark dark:placeholder:!text-auth-muted-dark dark:focus:!border-auth-accent',
            ]"
            class="w-full"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute start-3 top-1/2 size-4 -translate-y-1/2 text-auth-muted dark:text-auth-muted-dark"
              />
            </template>
          </Input>
        </div>
        <div class="flex flex-shrink-0 items-center gap-3">
          <div
            class="flex items-center gap-1 [&_button]:!rounded-lg [&_button]:transition-colors [&_button]:focus-visible:!outline [&_button]:focus-visible:!outline-2 [&_button]:focus-visible:!outline-offset-2 [&_button]:focus-visible:!outline-auth-action dark:[&_button]:focus-visible:!outline-auth-accent"
          >
            <div v-if="!isLabelView && !isActiveView" class="relative">
              <Button
                id="toggleContactsFilterButton"
                :icon="
                  isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'
                "
                color="slate"
                size="sm"
                class="relative !size-9 !bg-auth-soft !text-auth-muted hover:!bg-auth-border hover:!text-auth-text dark:!bg-auth-soft-dark dark:!text-auth-muted-dark dark:hover:!bg-auth-border-dark dark:hover:!text-auth-text-dark"
                variant="ghost"
                @click="emit('filter')"
              >
                <div
                  v-if="hasActiveFilters && !isSegmentsView"
                  class="absolute end-0.5 top-0.5 size-2 rounded-full bg-auth-action dark:bg-auth-accent"
                />
              </Button>
              <slot name="filter" />
            </div>
            <Button
              v-if="
                hasActiveFilters &&
                !isSegmentsView &&
                !isLabelView &&
                !isActiveView
              "
              icon="i-lucide-save"
              color="slate"
              size="sm"
              variant="ghost"
              class="!size-9 !bg-auth-soft !text-auth-muted hover:!bg-auth-border hover:!text-auth-text dark:!bg-auth-soft-dark dark:!text-auth-muted-dark dark:hover:!bg-auth-border-dark dark:hover:!text-auth-text-dark"
              @click="emit('createSegment')"
            />
            <Button
              v-if="isSegmentsView && !isLabelView && !isActiveView"
              icon="i-lucide-trash"
              color="slate"
              size="sm"
              variant="ghost"
              class="!size-9 !bg-auth-soft !text-auth-muted hover:!bg-auth-border hover:!text-auth-text dark:!bg-auth-soft-dark dark:!text-auth-muted-dark dark:hover:!bg-auth-border-dark dark:hover:!text-auth-text-dark"
              @click="emit('deleteSegment')"
            />
            <ContactSortMenu
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
            <ContactMoreActions
              @add="emit('add')"
              @import="emit('import')"
              @export="emit('export')"
            />
          </div>
          <div class="h-5 w-px bg-auth-border dark:bg-auth-border-dark" />
          <ComposeConversation>
            <template #trigger>
              <Button
                :label="buttonLabel"
                size="sm"
                class="!h-9 !rounded-xl !bg-auth-action !px-4 !font-medium !text-white hover:!bg-auth-action-hover focus-visible:!outline-auth-action dark:!bg-auth-accent dark:!text-auth-brand dark:hover:!bg-auth-accent-hover dark:focus-visible:!outline-auth-accent"
              />
            </template>
          </ComposeConversation>
        </div>
      </div>
    </div>
  </header>
</template>
